import { inject, Injectable } from '@angular/core'
import { Action, Selector, State, StateContext } from '@ngxs/store'
import { SnackbarService } from '@saniup-webapps/shared'
import { PFLEGEBOX_PAUSCHALE, PflegeboxWarenkorbPosition, setzeMenge, verfuegbarerBetrag, warenkorbSumme } from '@saniup-webapps/shared/phm'
import { PflegeboxProdukt, PflegeboxProduktSku } from '../model/selfservice.model'
import { equalSku } from '../util/produkt.util'
import { CheckValidity, ResetState } from './global.actions'

/**
 * Die Rechenregeln des Warenkorbs (Höchstbetrag, Höchstmenge, Preis je Gebinde) liegen seit 08/2026
 * in `@saniup-webapps/shared/phm` — dasselbe benutzt das Leistungserbringer-Portal. Dieser State ist
 * nur noch die **Aufbewahrung**: er muss die Sitzung überleben, das Leistungserbringer-Portal hält
 * seinen Korb dagegen nur für die Dauer eines Dialogs.
 *
 * <p>Die gespeicherte Form (`sizeAndMaterial`/`amount`) bleibt wie sie ist: sie liegt im
 * localStorage jedes Patienten, und ein Umbenennen würde jeden offenen Warenkorb still leeren.
 */
function zuGeteilt(cart: ProduktCartStateModel['cart']): PflegeboxWarenkorbPosition[] {
  return cart.map((item) => ({ produkt: item.produkt, sku: item.sizeAndMaterial ?? {}, menge: item.amount }))
}

function ausGeteilt(positionen: PflegeboxWarenkorbPosition[]): ProduktCartStateModel['cart'] {
  return positionen.map((position) => ({
    produkt: position.produkt as PflegeboxProdukt,
    sizeAndMaterial: position.sku as PflegeboxProduktSku,
    amount: position.menge,
  }))
}

export interface ProduktCartStateModel {
  lastUpdated: Date | string
  cart: {
    produkt: PflegeboxProdukt
    sizeAndMaterial: PflegeboxProduktSku
    amount: number
  }[]
  bettschutzeinlage?: {
    produkt: PflegeboxProdukt
    sizeAndMaterial: PflegeboxProduktSku
  }
}

export class IncrementAmountOfProduktInCart {
  static readonly type = '[ProduktCart] Increment Produkt Amount'
  constructor(
    public produkt: PflegeboxProdukt,
    public amount: number,
    public sku: PflegeboxProduktSku,
  ) {}
}

export class UpdateProduktInCart {
  static readonly type = '[ProduktCart] Update Produkt'
  constructor(
    public produkt: PflegeboxProdukt,
    public amount: number,
    public sku: PflegeboxProduktSku,
  ) {}
}

export class SetBettschutzeinlage {
  static readonly type = '[ProduktCart] Set Bettschutzeinlage'
  constructor(
    public produkt: PflegeboxProdukt,
    public sku: PflegeboxProduktSku,
  ) {}
}
export class ClearBettschutzeinlage {
  static readonly type = '[ProduktCart] Clear Bettschutzeinlage'
}

export class ClearProduktCart {
  static readonly type = '[ProduktCart] Clear Cart'
}

function cartTotalGrossPrice(cart: ProduktCartStateModel['cart']) {
  return warenkorbSumme(zuGeteilt(cart))
}

@Injectable()
@State<ProduktCartStateModel>({
  name: 'produktCart',
  defaults: { lastUpdated: new Date(), cart: [], bettschutzeinlage: undefined },
})
export class ProduktCartState {
  private readonly snackbarService = inject(SnackbarService)

  @Selector()
  static getCart({ cart }: ProduktCartStateModel) {
    return cart
  }

  @Selector()
  static getBettschutzeinlage({ bettschutzeinlage }: ProduktCartStateModel) {
    return bettschutzeinlage
  }

  @Selector()
  static getCartTotalGrossPrice({ cart }: ProduktCartStateModel) {
    return cartTotalGrossPrice(cart)
  }

  @Selector()
  static showBiozideInfo({ cart }: ProduktCartStateModel) {
    return cart.some(() => true)
  }

  /**
   * Berechnet den verfügbaren Preis im Warenkorb.
   * @returns Der verfügbare Preis im Warenkorb.
   */
  @Selector()
  static getCartAvailablePrice({ cart }: ProduktCartStateModel) {
    return verfuegbarerBetrag(zuGeteilt(cart))
  }

  @Selector()
  static findProduktInCart(state: ProduktCartStateModel) {
    return (produkt: PflegeboxProdukt, sizeAndMaterial?: PflegeboxProduktSku) => {
      return state.cart.find((item) => item.produkt.id === produkt.id && equalSku(item.sizeAndMaterial, sizeAndMaterial))
    }
  }

  @Selector()
  static getState(state: ProduktCartStateModel) {
    return state
  }

  @Action(IncrementAmountOfProduktInCart)
  incProduktInCart(ctx: StateContext<ProduktCartStateModel>, { produkt, amount, sku }: IncrementAmountOfProduktInCart) {
    const cart = ctx.getState().cart
    const pI = cart.findIndex((item) => item.produkt.id === produkt.id && equalSku(item.sizeAndMaterial, sku))
    // Wenn Produkte gefunden
    if (pI !== -1) {
      ctx.dispatch(new UpdateProduktInCart(produkt, cart[pI].amount + amount, sku))
      return
    }
    return ctx.dispatch(new UpdateProduktInCart(produkt, amount, sku))
  }

  @Action(UpdateProduktInCart)
  incProduktToCart(ctx: StateContext<ProduktCartStateModel>, { produkt, amount, sku }: UpdateProduktInCart) {
    const { warenkorb, fehler } = setzeMenge(zuGeteilt(ctx.getState().cart), produkt, sku ?? {}, amount)

    // Abgelehnt heisst: der Warenkorb bleibt, wie er war. Den Satz dazu formuliert die App --
    // `libs/shared` kennt keine Meldungstexte.
    if (fehler === 'PAUSCHALE_UEBERSCHRITTEN') {
      this.snackbarService.showError(`Maximalbetrag von ${PFLEGEBOX_PAUSCHALE}€ überschritten.`)
      return
    }
    if (fehler === 'HOECHSTMENGE_ERREICHT') {
      this.snackbarService.showError(
        `Maximale Anzahl für ${produkt.name} überschritten. Maximal ${produkt.maxAllowedQuantity} Stück erlaubt.`,
      )
      return
    }

    ctx.patchState({ cart: ausGeteilt(warenkorb), lastUpdated: new Date() })
  }

  @Action(SetBettschutzeinlage)
  setBettschutzeinlage(ctx: StateContext<ProduktCartStateModel>, { produkt, sku }: SetBettschutzeinlage) {
    this.snackbarService.showSuccess('Der Artikel wurde Ihrer Box hinzugefügt.')
    ctx.patchState({
      bettschutzeinlage: {
        produkt,
        sizeAndMaterial: sku,
      },
    })
  }

  @Action(ClearBettschutzeinlage)
  clearBettschutzeinlage(ctx: StateContext<ProduktCartStateModel>) {
    this.snackbarService.showSuccess('Der Artikel wurde aus Ihrer Box entfernt.')
    ctx.patchState({
      bettschutzeinlage: undefined,
    })
  }

  @Action([ClearProduktCart, ResetState])
  clearProduktCart(ctx: StateContext<ProduktCartStateModel>) {
    ctx.setState({ cart: [], bettschutzeinlage: undefined, lastUpdated: new Date() })
  }

  @Action(CheckValidity)
  checkValidity(ctx: StateContext<ProduktCartStateModel>) {
    const { lastUpdated } = ctx.getState()
    // last update is more than 24 hours ago, reset cart
    if (new Date(lastUpdated).getTime() < Date.now() - 24 * 60 * 60 * 1000) {
      ctx.dispatch(new ClearProduktCart())
    }
  }
}
